import { useState, useEffect, useRef } from 'react';
import type { FC } from 'react';
import { AuroraLogo } from './Icons';
import './LoginScreen.css';

/** 用户信息 */
export interface UserInfo {
  id: string;
  email: string;
  name: string;
  avatar: string | null;
  role: string;
  plan: string;
  isDemo?: boolean;
  usage?: { tokens: number; requests: number; storage: number };
  createdAt?: number;
  lastLoginAt?: number;
}

interface LoginScreenProps {
  onLogin: (user: UserInfo, token: string) => void;
}

type AuthMode = 'login' | 'register';

/**
 * 登录 / 注册界面
 * 沉浸式极光动画背景 · 玻璃拟态卡片 · 全程动画反馈
 */
const LoginScreen: FC<LoginScreenProps> = ({ onLogin }) => {
  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mounted, setMounted] = useState(false);
  const [success, setSuccess] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  // 入场动画
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  // 极光粒子 canvas 背景
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let w = (canvas.width = window.innerWidth);
    let h = (canvas.height = window.innerHeight);

    const onResize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', onResize);

    // 浮动粒子
    const particles = Array.from({ length: 60 }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      r: Math.random() * 2 + 0.5,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      opacity: Math.random() * 0.4 + 0.1,
      twinkle: Math.random() * Math.PI * 2,
      color: ['0,122,255', '88,86,214', '100,210,255', '52,199,89'][Math.floor(Math.random() * 4)],
    }));

    // 流动极光带
    const ribbons = Array.from({ length: 4 }, (_, i) => ({
      color: ['0,122,255', '88,86,214', '100,210,255', '175,82,222'][i],
      phase: (i * Math.PI) / 2,
      speed: 0.15 + i * 0.08,
      amplitude: 60 + i * 30,
      yBase: h * (0.2 + i * 0.2),
      opacity: 0.06 - i * 0.01,
    }));

    const startTime = performance.now();

    const draw = () => {
      const t = (performance.now() - startTime) / 1000;

      // 深邃渐变背景
      const bg = ctx.createLinearGradient(0, 0, w, h);
      bg.addColorStop(0, '#07071a');
      bg.addColorStop(0.4, '#0a0a1f');
      bg.addColorStop(1, '#0d0518');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);

      // 星云光晕
      const nebula1 = ctx.createRadialGradient(w * 0.2, h * 0.3, 0, w * 0.2, h * 0.3, w * 0.45);
      nebula1.addColorStop(0, 'rgba(0,122,255,0.08)');
      nebula1.addColorStop(1, 'rgba(0,122,255,0)');
      ctx.fillStyle = nebula1;
      ctx.fillRect(0, 0, w, h);

      const nebula2 = ctx.createRadialGradient(w * 0.8, h * 0.2, 0, w * 0.8, h * 0.2, w * 0.4);
      nebula2.addColorStop(0, 'rgba(88,86,214,0.07)');
      nebula2.addColorStop(1, 'rgba(88,86,214,0)');
      ctx.fillStyle = nebula2;
      ctx.fillRect(0, 0, w, h);

      const nebula3 = ctx.createRadialGradient(w * 0.5, h * 0.85, 0, w * 0.5, h * 0.85, w * 0.5);
      nebula3.addColorStop(0, 'rgba(175,82,222,0.05)');
      nebula3.addColorStop(1, 'rgba(175,82,222,0)');
      ctx.fillStyle = nebula3;
      ctx.fillRect(0, 0, w, h);

      // 极光带
      ribbons.forEach(rb => {
        ctx.beginPath();
        ctx.moveTo(0, rb.yBase);
        const segs = 100;
        for (let i = 0; i <= segs; i++) {
          const x = (i / segs) * w;
          const y = rb.yBase + Math.sin(rb.phase + t * rb.speed + (i / segs) * Math.PI * 3) * rb.amplitude;
          i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.lineTo(w, rb.yBase + 200);
        ctx.lineTo(0, rb.yBase + 200);
        ctx.closePath();

        const grd = ctx.createLinearGradient(0, rb.yBase - rb.amplitude, 0, rb.yBase + rb.amplitude + 100);
        grd.addColorStop(0, `rgba(${rb.color},0)`);
        grd.addColorStop(0.4, `rgba(${rb.color},${rb.opacity})`);
        grd.addColorStop(1, `rgba(${rb.color},0)`);
        ctx.fillStyle = grd;
        ctx.fill();
      });

      // 粒子
      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        p.twinkle += 0.025;
        if (p.x < 0) p.x = w;
        if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h;
        if (p.y > h) p.y = 0;

        const alpha = p.opacity * (0.5 + 0.5 * Math.sin(p.twinkle));
        const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 5);
        glow.addColorStop(0, `rgba(${p.color},${alpha})`);
        glow.addColorStop(1, `rgba(${p.color},0)`);
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * 5, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = `rgba(${p.color},${alpha * 0.8})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      });

      animRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const handleModeSwitch = (m: AuthMode) => {
    if (m === mode) return;
    setMode(m);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const endpoint = mode === 'login' ? '/api/auth/login' : '/api/auth/register';
      const body = mode === 'login'
        ? { email, password }
        : { email, password, username: name };

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (!data.success) {
        setError(data.error || '操作失败');
        setLoading(false);
        return;
      }

      const serverUser = data.data.user;
      const userInfo: UserInfo = {
        id: serverUser.id,
        email: serverUser.email,
        name: serverUser.username || serverUser.name || serverUser.email.split('@')[0],
        avatar: serverUser.avatar ?? null,
        role: serverUser.role ?? 'user',
        plan: serverUser.role === 'admin' ? 'pro' : (serverUser.plan ?? 'free'),
      };

      localStorage.setItem('aurora_token', data.data.accessToken);
      localStorage.setItem('aurora_user', JSON.stringify(userInfo));

      setSuccess(true);
      setTimeout(() => onLogin(userInfo, data.data.accessToken), 600);
    } catch {
      setError('无法连接服务器，请检查网络后重试');
      setLoading(false);
    }
  };

  const handleQuickLogin = () => {
    const demoUser: UserInfo = {
      id: 'demo-user',
      email: 'demo@aurora.dev',
      name: 'Aurora 演示用户',
      avatar: null,
      role: 'user',
      isDemo: true,
      plan: 'pro',
      usage: { tokens: 0, requests: 0, storage: 0 },
      createdAt: Date.now(),
      lastLoginAt: Date.now(),
    };
    localStorage.setItem('aurora_token', 'demo-token-' + Date.now());
    localStorage.setItem('aurora_user', JSON.stringify(demoUser));
    setSuccess(true);
    setTimeout(() => onLogin(demoUser, 'demo-token'), 500);
  };

  return (
    <div className={`ls ${mounted ? 'ls--in' : ''} ${success ? 'ls--success' : ''}`}>
      {/* 极光 canvas 背景 */}
      <canvas ref={canvasRef} className="ls__canvas" />

      {/* 装饰光环 */}
      <div className="ls__halo ls__halo--1" />
      <div className="ls__halo ls__halo--2" />
      <div className="ls__halo ls__halo--3" />

      {/* 主卡片 */}
      <div className="ls__card">
        {/* 卡片顶部极光扫描线 */}
        <div className="ls__card-scanline" />

        {/* Logo 区 */}
        <div className="ls__logo-wrap">
          <div className="ls__logo-ring" />
          <div className="ls__logo-ring ls__logo-ring--2" />
          <div className="ls__logo-icon">
            <AuroraLogo size={40} />
          </div>
        </div>

        <div className="ls__heading">
          <h1 className="ls__title">Aurora</h1>
          <p className="ls__subtitle">极光智能 · 触手可及</p>
        </div>

        {/* 模式切换 Tab */}
        <div className="ls__tabs" role="tablist">
          <div className="ls__tabs-track">
            <div className={`ls__tabs-slider ${mode === 'register' ? 'ls__tabs-slider--right' : ''}`} />
          </div>
          <button
            className={`ls__tab ${mode === 'login' ? 'ls__tab--active' : ''}`}
            role="tab"
            aria-selected={mode === 'login'}
            onClick={() => handleModeSwitch('login')}
          >
            登录
          </button>
          <button
            className={`ls__tab ${mode === 'register' ? 'ls__tab--active' : ''}`}
            role="tab"
            aria-selected={mode === 'register'}
            onClick={() => handleModeSwitch('register')}
          >
            注册
          </button>
        </div>

        {/* 表单区 */}
        <form
          className={`ls__form ${mode === 'register' ? 'ls__form--register' : ''}`}
          onSubmit={handleSubmit}
        >
          {/* 用户名（注册时滑入） */}
          <div className={`ls__field-wrap ${mode === 'register' ? 'ls__field-wrap--visible' : 'ls__field-wrap--hidden'}`}>
            <div className={`ls__field ${focusedField === 'name' ? 'ls__field--focused' : ''}`}>
              <div className="ls__field-icon">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="5.5" r="3" stroke="currentColor" strokeWidth="1.4"/>
                  <path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
              </div>
              <input
                className="ls__input"
                type="text"
                placeholder="用户名"
                value={name}
                onChange={e => setName(e.target.value)}
                onFocus={() => setFocusedField('name')}
                onBlur={() => setFocusedField(null)}
                autoComplete="name"
                tabIndex={mode === 'register' ? 0 : -1}
              />
            </div>
          </div>

          {/* 邮箱 */}
          <div className={`ls__field ${focusedField === 'email' ? 'ls__field--focused' : ''}`}>
            <div className="ls__field-icon">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <rect x="1.5" y="3.5" width="13" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
                <path d="M1.5 5.5l6.5 4.5 6.5-4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
            </div>
            <input
              className="ls__input"
              type="email"
              placeholder="邮箱地址"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onFocus={() => setFocusedField('email')}
              onBlur={() => setFocusedField(null)}
              required
              autoComplete="email"
            />
          </div>

          {/* 密码 */}
          <div className={`ls__field ${focusedField === 'password' ? 'ls__field--focused' : ''}`}>
            <div className="ls__field-icon">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <rect x="3" y="7" width="10" height="7.5" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
                <path d="M5 7V5a3 3 0 116 0v2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
            </div>
            <input
              className="ls__input"
              type="password"
              placeholder={mode === 'register' ? '至少 8 位' : '密码'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              onFocus={() => setFocusedField('password')}
              onBlur={() => setFocusedField(null)}
              required
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            />
          </div>

          {/* 错误提示 */}
          {error && (
            <div className="ls__error" role="alert">
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="6.5" stroke="#FF453A" strokeWidth="1.4"/>
                <path d="M8 5v3.5M8 11v.5" stroke="#FF453A" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              {error}
            </div>
          )}

          {/* 提交按钮 */}
          <button
            className={`ls__submit ${loading ? 'ls__submit--loading' : ''} ${success ? 'ls__submit--success' : ''}`}
            type="submit"
            disabled={loading || success}
          >
            {success ? (
              <span className="ls__submit-check">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8l4 4 6-7" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
            ) : loading ? (
              <span className="ls__submit-spinner" />
            ) : (
              <span className="ls__submit-text">
                {mode === 'login' ? '登 录' : '注 册'}
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
            )}
          </button>
        </form>

        {/* 分隔线 */}
        <div className="ls__divider">
          <div className="ls__divider-line" />
          <span className="ls__divider-text">或</span>
          <div className="ls__divider-line" />
        </div>

        {/* 免登录体验 */}
        <button className="ls__guest" onClick={handleQuickLogin}>
          <span className="ls__guest-icon">⚡</span>
          免登录直接体验
        </button>

        <p className="ls__hint">跳过登录进入演示模式，功能受限</p>
      </div>

      {/* 底部版本 */}
      <div className="ls__version">Aurora v1.0.0 · BorealOS</div>
    </div>
  );
};

export default LoginScreen;
